import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMilestones } from '../../../api/milestones-client/milestones-client.hooks';
import {
  useDeleteOutcomeMutation,
  useOutcome,
  useOutcomes,
  useUpdateOutcomeMutation,
} from '../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setInitiativePerformance,
  setMilestonesData,
  setPerformanceActivePanel,
  setPerformanceTab,
} from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { useFlora } from '../../flora/flora.context';
import { FloraSupportedObjectTypes } from '../../flora/flora.type';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { toast } from '../../notifications';
import { GeneralViewNames } from '../../side-bar/side-bar.type';
import { MainSection as MainSectionCost } from '../initiative-performance/cost-tab/main-section/main-section';
import { MainSection as MainSectionFocus } from '../initiative-performance/focus-tab/main-section/main-section';
import { InitiativePerformanceFilters } from '../initiative-performance/initiative-performance-filters';
import { InitiativePerformanceSideSection } from '../initiative-performance/initiative-performance-side-section';
import { useSideSection } from '../initiative-performance/initiative-performance.hooks';
import { PerformanceTab } from '../initiative-performance/initiative-performance.type';
import { MainSection as MainSectionMilestones } from '../initiative-performance/milestones-tab/main-section/main-section';
import { MainSection as MainSectionRelatedTasks } from '../initiative-performance/related-tasks-tab/main-section/main-section';
import { MainSection as MainSectionThroughput } from '../initiative-performance/throughput-tab/main-section/main-section';
import { StrategyEntityModal } from '../strategy-entity-modal/strategy-entity-modal';
import { StrategyPerformanceControlBar } from '../strategy-performance/strategy-performance-control-bar';
import { StrategyPerformanceHeader } from '../strategy-performance/strategy-performance-header';
import { StrategyPerformanceInfoModal } from '../strategy-performance/strategy-performance-info-modal';
import { StrategyPerformanceOverview } from '../strategy-performance/strategy-performance-overview';
import { StrategyPerformanceTabs } from '../strategy-performance/strategy-performance-tabs';
import { transformOutcomeToInitiativeFormat } from '../strategy-performance/strategy-performance.hooks';
import { StrategyEntityType } from '../strategy.type';

export function OutcomeDetails() {
  useDocumentTitle('Objective Details - Bloomfilter');

  const i18n = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { outcome, query } = useOutcome(id);
  const { query: outcomesQuery } = useOutcomes();
  const { milestones } = useMilestones(outcome?.id);
  const performanceTab = useStrategyStore((state) => state.performanceTab);
  const performanceActivePanel = useStrategyStore((state) => state.performanceActivePanel);
  const showSideSection = useSideSection();
  const { setContextualInfo } = useFlora();

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const deleteOutcomeMutation = useDeleteOutcomeMutation({
    onSuccess: () => {
      setIsEditModalOpen(false);
      outcomesQuery.refetch();
      navigate('/application/strategy/outcomes');
      toast.success(`${i18n.t('common.outcome')} successfully deleted`);
    },
    onError: (error) => {
      toast.error(
        `Failed to delete ${i18n.t('common.outcome').toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    },
  });

  const updateOutcomeMutation = useUpdateOutcomeMutation({
    onSuccess: (data) => {
      setIsEditModalOpen(false);
      query.refetch();
      toast.success(`${i18n.t('common.outcome')} "${data.name}" updated successfully`);
    },
    onError: (error) => {
      toast.error(
        `Failed to update ${i18n.t('common.outcome').toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    },
  });

  useEffect(() => {
    if (outcome) {
      setContextualInfo({
        currentModule: GeneralViewNames.Strategy,
        currentObjects: [{ type: FloraSupportedObjectTypes.Objective, id: outcome.id, name: outcome.name }],
        startDate: outcome.start_date ?? null,
        endDate: outcome.end_date ?? null,
      });
    }
  }, [outcome, setContextualInfo]);

  useEffect(() => {
    // Set default tab to throughput
    setPerformanceTab(PerformanceTab.Throughput);
    setPerformanceActivePanel(Panel.Filters);
  }, []);

  useEffect(() => {
    if (query.error) {
      navigate('/application/strategy/outcomes');
    }
  }, [query.error, navigate]);

  // Transform and store outcome data for charts
  useEffect(() => {
    if (outcome) {
      const transformed = transformOutcomeToInitiativeFormat(outcome);
      setInitiativePerformance(transformed);
    }
    return () => {
      setInitiativePerformance(null);
    };
  }, [outcome]);

  useEffect(() => {
    if (milestones) {
      setMilestonesData(milestones);
    }
    return () => {
      setMilestonesData([]);
    };
  }, [milestones]);

  const showSideFilters = performanceActivePanel === Panel.Filters;
  const setShowSideFilters = (show: boolean) => {
    setPerformanceActivePanel(show ? Panel.Filters : null);
  };

  const renderMainSection = () => {
    switch (performanceTab) {
      case PerformanceTab.Throughput:
        return <MainSectionThroughput />;
      case PerformanceTab.Focus:
        return <MainSectionFocus />;
      case PerformanceTab.Cost:
        return <MainSectionCost />;
      case PerformanceTab.RelatedTasks:
        return <MainSectionRelatedTasks />;
      case PerformanceTab.Milestones:
        return <MainSectionMilestones />;
      default:
        return <MainSectionThroughput />;
    }
  };

  const handleUpdate = async (data: any) => {
    if (!outcome) return;
    await updateOutcomeMutation.mutateAsync({ outcomeId: outcome.id, data });
  };

  return (
    <MenuDrivenContainer
      header={
        <StrategyPerformanceHeader
          entity={outcome ?? null}
          entityType={StrategyEntityType.Outcome}
          onUpdate={() => query.refetch()}
        />
      }
    >
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" transitionDuration={30} />
      <PageLayout
        controlBar={
          <StrategyPerformanceControlBar
            entity={outcome ?? null}
            entityType="outcome"
            openInfoModal={setIsInfoModalOpen}
            openEditModal={setIsEditModalOpen}
          />
        }
        overview={{
          title: i18n.t('strategy.overview.title'),
          content: <StrategyPerformanceOverview entity={outcome ?? null} entityType="outcome" />,
        }}
        tabs={<StrategyPerformanceTabs entityType={StrategyEntityType.Outcome} />}
        sideFilters={{
          title: i18n.t('common.filters'),
          content: <InitiativePerformanceFilters setShow={setShowSideFilters} />,
          show: showSideFilters,
          setShow: setShowSideFilters,
        }}
        mainSection={renderMainSection()}
        sideSection={<InitiativePerformanceSideSection />}
        showSideSection={showSideSection}
        data-page-layout
      />
      <StrategyPerformanceInfoModal
        entity={outcome ?? null}
        opened={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
      {outcome && (
        <StrategyEntityModal
          mode="edit"
          entityType={StrategyEntityType.Outcome}
          entity={outcome}
          opened={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleUpdate}
          isSubmitting={updateOutcomeMutation.isPending}
          onDelete={() => deleteOutcomeMutation.mutateAsync(outcome.id)}
          isDeleting={deleteOutcomeMutation.isPending}
        />
      )}
    </MenuDrivenContainer>
  );
}
